import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { planApi } from '@/api/planApi'
import { useNotificationsStore } from '@/stores/notificationsStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { useCan } from '@/hooks/useCan'
import { morphToast } from '@/components/ui/Toast'
import { SiteShell } from '@/components/layout/SiteShell'
import { PlanSelector } from '@/components/billing/PlanSelector'
import { BillingDisabledBanner } from '@/components/billing/BillingDisabledBanner'
import { CancelPlanDialog } from '@/components/billing/CancelPlanDialog'
import { useBillingEnabled } from '@/hooks/useBillingEnabled'
import { redirectToCheckout } from '@/utils/checkout'
import type { ChangePlanRequest, Plan } from '@/api/types'

// PlanCardState is the same shape exported from PlanSelector — mirror it here to avoid circular import
interface PlanCardState {
  isSelected: boolean
  isCurrent: boolean
  isPopular: boolean
}

function formatDate(dateStr: string | null | undefined, locale: string): string {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString(locale, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })
}

function toAccessEndDate(dateStr: string | null | undefined) {
  if (!dateStr) return null
  const accessEndDate = new Date(dateStr)
  accessEndDate.setDate(accessEndDate.getDate() - 1)
  return accessEndDate.toISOString()
}

function resolvePlanEndDate(subscription: { current_period_end?: string | null; canceled_at?: string | null } | null | undefined) {
  if (subscription?.current_period_end) return toAccessEndDate(subscription.current_period_end)
  if (!subscription?.canceled_at) return null
  const fallbackDate = new Date(subscription.canceled_at)
  fallbackDate.setDate(fallbackDate.getDate() + 30)
  return toAccessEndDate(fallbackDate.toISOString())
}

export default function SubscriptionPage() {
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false)
  const [confirmationTimedOut, setConfirmationTimedOut] = useState(false)
  const billingNotifs = useNotificationsStore((s) => s.billing)
  const { t, i18n } = useTranslation()

  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace)
  const loadWorkspaceBySlug = useWorkspaceStore((s) => s.loadWorkspaceBySlug)
  const subscription = useWorkspaceStore((s) => s.subscription)
  const loadingSubscription = useWorkspaceStore((s) => s.loadingSubscription)
  const loadSubscription = useWorkspaceStore((s) => s.loadSubscription)
  const changePlan = useWorkspaceStore((s) => s.changePlan)
  const cancelSubscription = useWorkspaceStore((s) => s.cancelSubscription)
  const revertDowngrade = useWorkspaceStore((s) => s.revertDowngrade)

  const billingEnabled = useBillingEnabled()
  // Owner rights alone are not enough while payments are under development.
  const canChangePlan = useCan('change_plan') && billingEnabled
  const isOwner = useCan('change_plan')

  const [plans, setPlans] = useState<Plan[]>([])
  const [loadingPlans, setLoadingPlans] = useState(false)
  const [changingPlanId, setChangingPlanId] = useState<number | null>(null)
  const [reverting, setReverting] = useState(false)

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false)
  const [pendingPlan, setPendingPlan] = useState<Plan | null>(null) // null = full cancel to free
  const [dialogLoading, setDialogLoading] = useState(false)

  // CPF/CNPJ collection before checkout

  useEffect(() => {
    if (!workspaceSlug || currentWorkspace?.slug === workspaceSlug) return
    void loadWorkspaceBySlug(workspaceSlug).catch(() => {
      navigate('/workspaces', { replace: true })
    })
  }, [workspaceSlug, currentWorkspace?.slug, loadWorkspaceBySlug, navigate])

  useEffect(() => {
    if (!currentWorkspace?.id || currentWorkspace.slug !== workspaceSlug) return
    void loadSubscription(currentWorkspace.id)
  }, [currentWorkspace?.id, currentWorkspace?.slug, workspaceSlug, loadSubscription])

  // `t` do i18next troca de identidade a cada render. Mante-lo nas dependencias
  // de um efeito faz o efeito ser destruido e recriado o tempo todo — o que
  // matava o polling em voo antes da primeira iteracao. Fica num ref.
  const tRef = useRef(t)
  tRef.current = t
  const watchKeyRef = useRef<string | null>(null)

  /**
   * Retorno do checkout: o usuário volta ANTES de a Stripe confirmar o pagamento,
   * então há uma janela em que o plano ainda não vale. Mostramos uma tela dedicada
   * e saímos dela pelo push `billing.subscription_activated` do WebSocket.
   *
   * O polling é a REDE DE SEGURANÇA: se o socket estiver caído — ou se o push se
   * perder — o usuário ainda sai da espera sozinho. Ele checa ANTES de dormir,
   * porque o webhook pode já ter chegado enquanto o navegador redirecionava.
   */
  const checkoutStatus = searchParams.get('status')

  useEffect(() => {
    const workspaceId = currentWorkspace?.id
    if (!workspaceId || currentWorkspace.slug !== workspaceSlug || !checkoutStatus) return

    if (checkoutStatus === 'cancelled') {
      morphToast.warning(
        tRef.current('billing.checkoutCancelled', 'Pagamento não concluído. Seu plano não foi alterado.'),
      )
      setSearchParams({}, { replace: true })
      return
    }
    if (checkoutStatus !== 'success') return

    // Guarda por ref: o watcher roda UMA vez por retorno de checkout. Sem isto,
    // qualquer dependencia que troque de identidade entre renders derruba o
    // watcher em voo e recomeca — foi assim que a tela ficou presa antes. Em vez
    // de caçar qual dep oscila, o inicio deixa de depender disso.
    const runKey = `${workspaceId}:${checkoutStatus}`
    if (watchKeyRef.current === runKey) return
    watchKeyRef.current = runKey

    setAwaitingConfirmation(true)
    let disposed = false

    function finish() {
      disposed = true
      watchKeyRef.current = null
      setAwaitingConfirmation(false)
      setConfirmationTimedOut(false)
      setSearchParams({}, { replace: true })
    }

    function isConfirmed() {
      const latest = useWorkspaceStore.getState().subscription
      return latest?.status === 'active' && !latest.pending_plan_code
    }

    async function watchUntilConfirmed() {
      for (let attempt = 0; attempt < 20 && !disposed; attempt += 1) {
        await loadSubscription(workspaceId)
        if (disposed) return
        if (isConfirmed()) {
          // Encerra aqui mesmo em vez de depender de outro efeito reagir: se a
          // condicao de saida morar so em outro lugar, uma divergencia entre as
          // duas prende a tela — foi exatamente o que aconteceu.
          finish()
          return
        }
        await new Promise((resolve) => window.setTimeout(resolve, 3000))
      }
      if (!disposed) setConfirmationTimedOut(true)
    }

    void watchUntilConfirmed()
    // Sem cleanup que aborte: o watcher e dono do proprio ciclo (guarda por ref +
    // `finish`). Abortar em cada re-render foi exatamente o bug anterior.
    return undefined
  }, [checkoutStatus, currentWorkspace?.id, currentWorkspace?.slug, loadSubscription, setSearchParams, workspaceSlug])

  /** Caminho rápido: o push do WS chega e antecipa o recarregamento. */
  useEffect(() => {
    if (!awaitingConfirmation || !currentWorkspace?.id) return
    const activated = billingNotifs.find(
      (b) => b.kind === 'activated' && b.workspace_id === currentWorkspace.id,
    )
    if (!activated) return
    void loadSubscription(currentWorkspace.id)
  }, [awaitingConfirmation, billingNotifs, currentWorkspace?.id, loadSubscription])

  /** Saída também reativa ao store, para o caso do push chegar entre as sondagens. */
  useEffect(() => {
    if (!awaitingConfirmation) return
    if (subscription?.status !== 'active' || subscription.pending_plan_code) return
    watchKeyRef.current = null
    setAwaitingConfirmation(false)
    setConfirmationTimedOut(false)
    setSearchParams({}, { replace: true })
  }, [awaitingConfirmation, subscription?.status, subscription?.pending_plan_code, setSearchParams])



  useEffect(() => {
    setLoadingPlans(true)
    planApi
      .list()
      .then((list) => setPlans(list.filter((p) => p.is_active)))
      .catch(() => {/* ignore */})
      .finally(() => setLoadingPlans(false))
  }, [])

  const currentPlanCode = subscription?.plan.code ?? null
  const isPaidPlan = currentPlanCode != null && currentPlanCode !== 'free'
  const isCanceled = subscription?.cancel_at_period_end === true
  const freePlan = plans.find((p) => p.code.toLowerCase() === 'free') ?? null

  function isDowngrade(targetPlan: Plan): boolean {
    if (!subscription) return false
    return (targetPlan.price_cents ?? 0) < (subscription.plan.price_cents ?? 0)
  }

  /** Run a change-plan request; if it returns a checkout URL, redirect to it. */
  async function applyChange(plan: Plan, body: ChangePlanRequest): Promise<boolean> {
    const result = await changePlan(body)
    if (result.checkout_url) {
      redirectToCheckout(result.checkout_url)
      return true
    }
    if (result.pending_confirmation) {
      // Upgrade que exigiu autenticacao (3DS): o plano so vale quando o webhook
      // confirmar, entao nao anunciamos "plano alterado" — esperamos.
      setAwaitingConfirmation(true)
      return true
    }
    morphToast.success(t('billing.planChanged', `Plano alterado para ${plan.name}`))
    return false
  }

  async function handleConfirmDialog() {
    if (!canChangePlan) return
    setDialogLoading(true)
    try {
      if (pendingPlan === null) {
        await cancelSubscription()
        morphToast.success(
          t('billing.cancelSuccess', 'Assinatura cancelada. O plano ficará ativo até o final do período.'),
        )
      } else {
        setChangingPlanId(pendingPlan.id)
        await applyChange(pendingPlan, { plan_code: pendingPlan.code })
      }
    } catch (err) {
      morphToast.fromError(err, t('common.error', 'Erro'))
    } finally {
      setDialogLoading(false)
      setChangingPlanId(null)
      setDialogOpen(false)
    }
  }

  async function handleRevertDowngrade() {
    if (!canChangePlan) return
    setReverting(true)
    try {
      await revertDowngrade()
      morphToast.success(t('billing.revertSuccess', 'Troca de plano cancelada.'))
    } catch (err) {
      morphToast.fromError(err, t('common.error', 'Erro'))
    } finally {
      setReverting(false)
    }
  }

  async function handleRetryPayment() {
    if (!canChangePlan || !subscription) return
    setChangingPlanId(subscription.plan.id)
    try {
      // Re-selecting the current paid plan while PAST_DUE starts a fresh checkout.
      await applyChange(subscription.plan, { plan_code: subscription.plan.code })
    } catch (err) {
      morphToast.fromError(err, t('common.error', 'Erro'))
    } finally {
      setChangingPlanId(null)
    }
  }

  async function handleSelectPlan(plan: Plan) {
    if (!canChangePlan) return
    if (subscription?.plan.code === plan.code) return
    if (
      plan.code.toLowerCase() !== 'free'
      && plan.is_available_for_purchase === false
    ) return

    if (plan.code.toLowerCase() === 'free' && isPaidPlan) {
      // Cancel → open warning dialog
      setPendingPlan(null)
      setDialogOpen(true)
      return
    }

    if (isPaidPlan && isDowngrade(plan)) {
      // Paid-to-paid downgrade → open warning dialog (native change-plan, no checkout)
      setPendingPlan(plan)
      setDialogOpen(true)
      return
    }

    // Qualquer contratacao vai direto ao checkout hospedado: e la que o pagador
    // informa cartao e dados. Nao pedimos CPF/CNPJ do nosso lado.
    setChangingPlanId(plan.id)
    try {
      await applyChange(plan, { plan_code: plan.code })
    } catch (err) {
      morphToast.fromError(err, t('common.error', 'Erro'))
    } finally {
      setChangingPlanId(null)
    }
  }

  function renderActionButton(plan: Plan, state: PlanCardState) {
    const { isCurrent } = state
    const isFree = plan.code.toLowerCase() === 'free'
    const isLoading = changingPlanId === plan.id
    const isUnavailable = !isFree && plan.is_available_for_purchase === false

    // ── Current paid plan card ────────────────────────────────────────────
    if (isCurrent && isPaidPlan) {
      const planEndDate = formatDate(resolvePlanEndDate(subscription), i18n.language)
      const renewalDate = formatDate(subscription?.current_period_end, i18n.language)

      if (isCanceled) {
        return (
          <div
            style={{
              width: '100%',
              minHeight: 72,
              borderRadius: 14,
              border: '1px solid var(--color-danger-border)',
              background: 'var(--color-danger-bg)',
              color: 'var(--color-danger)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              padding: '12px 14px',
              lineHeight: 1.4,
              marginTop: 'auto',
              gap: 4,
            }}
          >
            <strong style={{ fontSize: 13, fontWeight: 700 }}>
              {t('billing.cancelScheduled', 'Cancelamento agendado')}
            </strong>
            <span style={{ fontSize: 12 }}>
              {t('billing.planEndsOn', 'Você ainda tem um pouco de tempo, seu plano acaba em {{date}}', {
                date: planEndDate,
              })}
            </span>
          </div>
        )
      }

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 'auto', paddingTop: 8 }}>
          <div
            style={{
              width: '100%',
              minHeight: 48,
              borderRadius: 14,
              border: '1px solid var(--teal)',
              background: 'transparent',
              color: 'var(--text)',
              fontSize: 14,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {t('billing.currentBadge', 'Plano atual')}
          </div>
          <p
            style={{
              margin: 0,
              fontSize: 12,
              color: 'var(--teal)',
              textAlign: 'center',
              lineHeight: 1.4,
            }}
          >
            {t('billing.renewsOn', 'Renova em {{date}}', {
              date: renewalDate,
            })}
          </p>
        </div>
      )
    }

    // ── Free card while on a paid plan (not yet canceled) → Cancel button ─
    if (isFree && isPaidPlan && !isCanceled) {
      return (
        <button
          disabled={!canChangePlan || changingPlanId !== null}
          onClick={(e) => {
            e.stopPropagation()
            setPendingPlan(null)
            setDialogOpen(true)
          }}
          style={{
            width: '100%',
            minHeight: 48,
            borderRadius: 14,
            border: '1px solid var(--color-danger-border)',
            background: 'transparent',
            color: 'var(--color-danger)',
            fontSize: 14,
            fontWeight: 700,
            cursor: !canChangePlan ? 'default' : 'pointer',
            opacity: !canChangePlan ? 0.45 : 1,
            marginTop: 'auto',
          }}
        >
          {t('billing.cancelPlan', 'Cancelar plano')}
        </button>
      )
    }

    // ── Free card — cancelation already scheduled ─────────────────────────
    if (isFree && isPaidPlan && isCanceled) {
      return (
        <div
          style={{
            width: '100%',
            minHeight: 48,
            borderRadius: 14,
            border: '1px solid var(--color-danger-border)',
            background: 'var(--color-danger-bg)',
            color: 'var(--color-danger)',
            fontSize: 13,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: '8px 12px',
            lineHeight: 1.4,
            marginTop: 'auto',
          }}
        >
          {t('billing.cancelScheduled', 'Cancelamento agendado')}
        </div>
      )
    }

    // ── Free card while already on free plan ──────────────────────────────
    if (isCurrent && !isPaidPlan) {
      return (
        <div
          style={{
            width: '100%',
            minHeight: 48,
            borderRadius: 14,
            border: '1px solid var(--accent-border)',
            background: 'transparent',
            color: 'var(--text)',
            fontSize: 14,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: 'auto',
          }}
        >
          {t('billing.currentBadge', 'Plano atual')}
        </div>
      )
    }

    // ── Upgrade / paid-to-paid downgrade button ───────────────────────────
    const down = isPaidPlan && isDowngrade(plan)
    return (
      <button
        disabled={!canChangePlan || changingPlanId !== null || isUnavailable}
        onClick={(e) => {
          e.stopPropagation()
          void handleSelectPlan(plan)
        }}
        style={{
          width: '100%',
          minHeight: 48,
          borderRadius: 14,
          border: '1px solid var(--accent)',
          background: 'var(--accent)',
          color: '#fff',
          fontSize: 14,
          fontWeight: 700,
          cursor: !canChangePlan || changingPlanId !== null || isUnavailable ? 'default' : 'pointer',
          opacity: !canChangePlan || isUnavailable ? 0.45 : 1,
          marginTop: 'auto',
        }}
      >
        {isLoading
          ? t('common.loading', 'Aguarde...')
          : !billingEnabled || isUnavailable
          ? t('billing.disabled.action', 'Em breve')
          : down
          ? t('billing.downgrade', 'Fazer downgrade')
          : t('billing.upgrade', 'Selecionar')}
      </button>
    )
  }

  // Tela de espera: ocupa a página inteira porque, enquanto o pagamento não é
  // confirmado, qualquer ação de plano abaixo estaria operando sobre um estado
  // que está prestes a mudar.
  if (awaitingConfirmation) {
    return (
      <SiteShell>
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--bg-app)',
            color: 'var(--text)',
            fontFamily: 'system-ui, sans-serif',
            padding: 24,
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 460,
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: 12,
              padding: 40,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 20,
              textAlign: 'center',
            }}
          >
            {confirmationTimedOut ? (
              <>
                <div style={{ fontSize: 40 }}>⏳</div>
                <h1 style={{ margin: 0, fontSize: 20 }}>
                  {t('billing.await.slowTitle', 'A confirmação está demorando')}
                </h1>
                <p style={{ margin: 0, fontSize: 14, opacity: 0.75, lineHeight: 1.6 }}>
                  {t(
                    'billing.await.slowBody',
                    'Seu pagamento foi enviado, mas ainda não recebemos a confirmação. Não é preciso pagar de novo — assim que ela chegar, o plano é ativado automaticamente e você recebe uma notificação.',
                  )}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setAwaitingConfirmation(false)
                    setConfirmationTimedOut(false)
                    setSearchParams({}, { replace: true })
                  }}
                  style={{
                    background: 'var(--accent)',
                    border: 'none',
                    color: '#fff',
                    borderRadius: 8,
                    padding: '10px 18px',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {t('billing.await.backToPlans', 'Voltar para os planos')}
                </button>
              </>
            ) : (
              <>
                <div
                  aria-hidden
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    border: '3px solid var(--border)',
                    borderTopColor: 'var(--accent)',
                    animation: 'morph-spin 0.9s linear infinite',
                  }}
                />
                <style>{'@keyframes morph-spin { to { transform: rotate(360deg) } }'}</style>
                <h1 style={{ margin: 0, fontSize: 20 }}>
                  {t('billing.await.title', 'Confirmando seu pagamento')}
                </h1>
                <p style={{ margin: 0, fontSize: 14, opacity: 0.75, lineHeight: 1.6 }}>
                  {t(
                    'billing.await.body',
                    'Estamos aguardando a confirmação do provedor de pagamento. Isso costuma levar alguns segundos — pode deixar esta tela aberta.',
                  )}
                </p>
                <span style={{ fontSize: 12, opacity: 0.5 }}>
                  {t('billing.await.hint', 'Não feche nem recarregue a página.')}
                </span>
                {/* Saída sempre disponível: uma tela sem porta é o pior resultado
                    possível se a confirmação não vier. */}
                <button
                  type="button"
                  onClick={() => {
                    watchKeyRef.current = null
                    setAwaitingConfirmation(false)
                    setConfirmationTimedOut(false)
                    setSearchParams({}, { replace: true })
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--accent)',
                    fontSize: 13,
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  {t('billing.await.backToPlans', 'Voltar para os planos')}
                </button>
              </>
            )}
          </div>
        </div>
      </SiteShell>
    )
  }

  return (
    <>
      <SiteShell>
        <div
          style={{
            flex: 1,
            background: 'var(--bg-app)',
            color: 'var(--text)',
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '24px 32px 16px',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <div>
              <h1 style={{ margin: 0, fontSize: 22 }}>
                {t('billing.title', 'Plano e assinatura')}
              </h1>
              <p style={{ margin: '4px 0 0', fontSize: 13, opacity: 0.5 }}>
                {currentWorkspace?.name}
              </p>
            </div>
            <button
              onClick={() => navigate(`/workspace/${workspaceSlug}/projects`)}
              style={{
                background: 'transparent',
                border: '1px solid var(--border)',
                color: 'var(--text)',
                borderRadius: 8,
                padding: '8px 16px',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              {t('common.back', 'Voltar')}
            </button>
          </div>

          <div style={{ padding: '24px 32px', maxWidth: 1000 }}>
            {!billingEnabled ? (
              <div style={{ marginBottom: 16 }}>
                <BillingDisabledBanner />
              </div>
            ) : null}

            {billingEnabled && !isOwner ? (
              <div style={{ fontSize: 12, opacity: 0.5, marginBottom: 16 }}>
                {t('billing.onlyOwner', 'Apenas o OWNER do workspace pode alterar o plano.')}
              </div>
            ) : null}

            {subscription?.past_due_since ? (
              <div
                role="alert"
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  marginBottom: 16,
                  padding: '12px 16px',
                  borderRadius: 12,
                  border: '1px solid var(--color-danger-border)',
                  background: 'var(--color-danger-bg)',
                  color: 'var(--color-danger)',
                  fontSize: 13,
                }}
              >
                <span>
                  {t('billing.pastDue', 'O pagamento da sua assinatura falhou. Atualize o cartão para não perder o acesso.')}
                </span>
                {canChangePlan ? (
                  <button
                    onClick={() => void handleRetryPayment()}
                    disabled={changingPlanId !== null}
                    style={{
                      borderRadius: 8,
                      border: '1px solid var(--color-danger-border)',
                      background: 'transparent',
                      color: 'var(--color-danger)',
                      padding: '6px 12px',
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: changingPlanId !== null ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {t('billing.updatePayment', 'Atualizar pagamento')}
                  </button>
                ) : null}
              </div>
            ) : null}

            {subscription?.pending_plan_code && subscription.scheduled_change_at ? (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  marginBottom: 16,
                  padding: '12px 16px',
                  borderRadius: 12,
                  border: '1px solid var(--accent-border)',
                  background: 'transparent',
                  fontSize: 13,
                }}
              >
                <span>
                  {t('billing.scheduledChange', 'Seu plano muda para {plan} em {date}.', {
                    plan:
                      plans.find((p) => p.code.toLowerCase() === subscription.pending_plan_code?.toLowerCase())?.name ??
                      subscription.pending_plan_code,
                    date: formatDate(subscription.scheduled_change_at, i18n.language),
                  })}
                </span>
                {canChangePlan ? (
                  <button
                    onClick={() => void handleRevertDowngrade()}
                    disabled={reverting}
                    style={{
                      borderRadius: 8,
                      border: '1px solid var(--accent)',
                      background: 'transparent',
                      color: 'var(--accent)',
                      padding: '6px 12px',
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: reverting ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {reverting ? t('common.loading', 'Aguarde...') : t('billing.revertChange', 'Manter plano atual')}
                  </button>
                ) : null}
              </div>
            ) : null}

            {subscription?.document_masked ? (
              <div style={{ fontSize: 12, opacity: 0.5, marginBottom: 16 }}>
                {t('billing.documentOnFile', 'Documento de cobrança: {doc}', { doc: subscription.document_masked })}
              </div>
            ) : null}

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <PlanSelector
                plans={plans}
                loading={loadingPlans || loadingSubscription}
                currentPlanCode={currentPlanCode}
                selectedPlanCode={currentPlanCode}
                title={t('billing.dialog.selectorTitle')}
                subtitle={t('billing.dialog.selectorSubtitle')}
                onSelect={canChangePlan ? (plan) => void handleSelectPlan(plan) : undefined}
                renderAction={(plan, state) => renderActionButton(plan, state)}
              />
            </div>
          </div>
        </div>
      </SiteShell>

      {/* Rendered outside SiteShell so position:fixed works regardless of CSS transforms */}
      {dialogOpen && subscription ? (
        <CancelPlanDialog
          open={dialogOpen}
          subscription={subscription}
          currentPlan={subscription.plan}
          targetPlan={pendingPlan}
          freePlan={freePlan}
          onConfirm={() => void handleConfirmDialog()}
          onClose={() => {
            if (!dialogLoading) setDialogOpen(false)
          }}
          loading={dialogLoading}
        />
      ) : null}
    </>
  )
}
